import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { Button } from '@/components/primitives/Button';
import { Select } from '@/components/primitives/Select';
import { useAppContext } from '@/context/AppContext';
import { useGroupedReport } from '@/hooks/useGroupedReport';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useEmployees } from '@/hooks/useEmployees';
import type { GroupDimension } from '@/types/reports';
import styles from './GroupedReportPage.module.css';

// Task 160 §Q1 — recommended preset list, applied by default per the
// /orchestrate plan. If the operator decides on a different list, the chip
// metadata below is the only place to change.
interface Preset {
  id: string;
  label: string;
  primary: GroupDimension;
  secondary: GroupDimension;
}
const PRESETS: readonly Preset[] = [
  { id: 'date-project', label: 'Date → Project', primary: 'date', secondary: 'project' },
  { id: 'project-date', label: 'Project → Date', primary: 'project', secondary: 'date' },
  {
    id: 'application-project',
    label: 'Application → Project',
    primary: 'application',
    secondary: 'project',
  },
] as const;

const DIMENSION_OPTIONS: ReadonlyArray<{ value: GroupDimension; label: string }> = [
  { value: 'date', label: 'Date' },
  { value: 'client', label: 'Client' },
  { value: 'project', label: 'Project' },
  { value: 'application', label: 'Application' },
  { value: 'employee', label: 'Employee' },
];

const FILTER_ALL = '';

export function GroupedReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mockupMode, currentOrg, dateRange } = useAppContext();

  // Period comes from the global top-bar DateRangePicker via AppContext —
  // mirrors the Applications and Domain Analytics pages. The page does not
  // host its own period selector. The grouped backend route accepts the
  // monthly report's full period grammar; we always emit `period=custom`
  // because the global picker stores a `{ start, end }` literal.
  const periodQs = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return null;
    const params = new URLSearchParams();
    params.set('period', 'custom');
    params.set('startDate', dateRange.start);
    params.set('endDate', dateRange.end);
    return params.toString();
  }, [dateRange.start, dateRange.end]);

  const primary = (searchParams.get('primary') ?? 'date') as GroupDimension;
  const secondary = (searchParams.get('secondary') ?? 'project') as GroupDimension;

  const clientId = searchParams.get('clientId') ?? FILTER_ALL;
  const projectId = searchParams.get('projectId') ?? FILTER_ALL;
  const employeeId = searchParams.get('employeeId') ?? FILTER_ALL;

  const { data: clients } = useClients();
  const { data: projects } = useProjects();
  const { data: employees } = useEmployees();

  const { data: report, isLoading } = useGroupedReport({
    primary,
    secondary,
    periodQs: mockupMode ? null : periodQs,
    clientId: clientId || undefined,
    projectId: projectId || undefined,
    employeeId: employeeId || undefined,
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const togglePrimary = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const onPickPreset = (preset: Preset) => {
    const next = new URLSearchParams(searchParams);
    next.set('primary', preset.primary);
    next.set('secondary', preset.secondary);
    setSearchParams(next);
  };

  const onPickPrimary = (val: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('primary', val);
    // Secondary cannot equal primary; if it would, swap the two so the user's
    // existing pick survives in the inverted slot.
    if (secondary === (val as GroupDimension)) {
      next.set('secondary', primary);
    }
    setSearchParams(next);
  };

  const onPickSecondary = (val: string) => {
    if (val === primary) return;
    updateParam('secondary', val);
  };

  const onPickClient = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (val === FILTER_ALL) {
      next.delete('clientId');
      // Clear project too — project options were filtered by client.
      next.delete('projectId');
    } else {
      next.set('clientId', val);
      // If the currently picked project belongs to a different client, drop it.
      if (projectId) {
        const proj = projects.find((p) => p.id === projectId);
        if (proj && proj.clientId !== val) next.delete('projectId');
      }
    }
    setSearchParams(next);
  };

  const filteredProjects = useMemo(
    () => (clientId ? projects.filter((p) => p.clientId === clientId) : projects),
    [projects, clientId],
  );

  const csvHref = useMemo(() => {
    if (!periodQs) return null;
    const params = new URLSearchParams(periodQs);
    params.set('primary', primary);
    params.set('secondary', secondary);
    if (clientId) params.set('clientId', clientId);
    if (projectId) params.set('projectId', projectId);
    if (employeeId) params.set('employeeId', employeeId);
    return `/api/v1/orgs/${currentOrg.id}/reports/grouped.csv?${params.toString()}`;
  }, [periodQs, primary, secondary, clientId, projectId, employeeId, currentOrg.id]);

  const onExportCsv = () => {
    if (!csvHref) return;
    window.open(csvHref, '_blank');
  };

  const activePresetId =
    PRESETS.find((p) => p.primary === primary && p.secondary === secondary)?.id ?? null;

  // Task 160 §Q3 — secondary dropdown excludes the primary value.
  const secondaryOptions = DIMENSION_OPTIONS.filter((o) => o.value !== primary);

  const rows = report?.rows ?? [];
  const summary = report?.summary;

  return (
    <>
      <PageHeader
        title="Grouped Time Report"
        subtitle="Two-level breakdown for invoice review — pick a preset or build your own."
      />

      <div className={styles.reportControls}>
        <div className={styles.presetRow} data-testid="grouped-presets">
          <span className={styles.controlsLabel}>Presets:</span>
          {PRESETS.map((preset) => (
            // Task 162 D5 — Rule 19 fix: preset toggles render via the
            // <Button> primitive's `chip` variant. Active state is driven
            // by `aria-pressed`, which the variant CSS keys off.
            <Button
              key={preset.id}
              variant="chip"
              size="sm"
              onClick={() => onPickPreset(preset)}
              aria-pressed={activePresetId === preset.id}
              data-testid={`grouped-preset-${preset.id}`}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className={styles.controlsRow}>
          <Select
            value={primary}
            options={DIMENSION_OPTIONS}
            onChange={onPickPrimary}
            label="Group by"
            size="sm"
          />
          <Select
            value={secondary}
            options={secondaryOptions}
            onChange={onPickSecondary}
            label="Then by"
            size="sm"
          />

          <Select
            value={clientId || FILTER_ALL}
            options={[
              { value: FILTER_ALL, label: 'All clients' },
              ...clients.map((c) => ({ value: c.id, label: c.name })),
            ]}
            onChange={onPickClient}
            label="Client"
            size="sm"
          />
          <Select
            value={projectId || FILTER_ALL}
            options={[
              { value: FILTER_ALL, label: 'All projects' },
              ...filteredProjects.map((p) => ({ value: p.id, label: p.name })),
            ]}
            onChange={(v) => updateParam('projectId', v || null)}
            label="Project"
            size="sm"
          />
          <Select
            value={employeeId || FILTER_ALL}
            options={[
              { value: FILTER_ALL, label: 'All employees' },
              ...employees.map((e) => ({ value: e.id, label: e.name })),
            ]}
            onChange={(v) => updateParam('employeeId', v || null)}
            label="Employee"
            size="sm"
          />

          <div className={styles.actionsRow}>
            <Button
              variant="secondary"
              onClick={onExportCsv}
              size="sm"
              disabled={!csvHref || isLoading}
            >
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Task 162 D4 — Rule 9 exception (same shape as ReportsMonthlyPage):
          the primary→secondary hierarchical row structure cannot be
          expressed by DataTable's flat-row model without a structural
          extension. Decision deferred until usage demonstrates a shared
          HierarchicalDataTable is warranted (see task 162 §Decisions).
          Rule 4 carve-out: the "no activity" empty state lives inside the
          <tbody> rather than being rendered via <EmptyState> outside the
          table so the picker bar, headers, and any future <tfoot> chrome
          stay visible while the user adjusts the controls. */}
      <div className={styles.tableWrap} data-testid="grouped-report-table">
        <table className={styles.table}>
          <caption className={styles.srOnly}>
            Two-level grouped time report by {primary} and {secondary}
          </caption>
          <thead>
            <tr>
              {/* Task 162 D6 — Rule 13: sortable headers intentionally
                  omitted. The grouped report's two-level structure makes a
                  per-column sort non-obvious (sort applies to which level?)
                  and the default ordering coming from the backend is the
                  meaningful one for invoice review. Revisit if usage shows
                  users want to re-rank within a level. */}
              <th>
                {labelFor(primary)} / {labelFor(secondary)}
              </th>
              <th className={styles.right}>Billable</th>
              <th className={styles.right}>Non-Billable</th>
              <th className={styles.right}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.emptyState}>
                  {isLoading ? 'Loading…' : 'No activity in this window.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isOpen = !!expanded[row.primaryKey];
                return (
                  <RowGroup
                    key={row.primaryKey}
                    row={row}
                    isOpen={isOpen}
                    onToggle={() => togglePrimary(row.primaryKey)}
                  />
                );
              })
            )}
          </tbody>
          {summary && rows.length > 0 ? (
            <tfoot>
              <tr>
                <td>Grand Total</td>
                <td className={styles.mono}>{summary.billableHours.toFixed(1)}h</td>
                <td className={styles.mono}>{summary.nonBillableHours.toFixed(1)}h</td>
                <td className={styles.mono}>{summary.totalHours.toFixed(1)}h</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </>
  );
}

function labelFor(dim: GroupDimension): string {
  const found = DIMENSION_OPTIONS.find((o) => o.value === dim);
  return found?.label ?? dim;
}

interface RowGroupProps {
  row: import('@/types/reports').GroupedReportRow;
  isOpen: boolean;
  onToggle: () => void;
}

function RowGroup({ row, isOpen, onToggle }: RowGroupProps) {
  return (
    <>
      <tr
        className={styles.rowPrimary}
        onClick={onToggle}
        data-testid={`grouped-primary-${row.primaryKey}`}
      >
        <td>
          <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>&#9654;</span>
          {row.primaryLabel}
        </td>
        <td className={styles.mono}>{row.billableHours.toFixed(1)}h</td>
        <td className={styles.mono}>{row.nonBillableHours.toFixed(1)}h</td>
        <td className={styles.mono}>{row.totalHours.toFixed(1)}h</td>
      </tr>
      {isOpen
        ? row.secondary.map((sec) => (
            <tr
              key={sec.key}
              className={styles.rowSecondary}
              data-testid={`grouped-secondary-${row.primaryKey}-${sec.key}`}
            >
              <td className={styles.secondaryLabel}>{sec.label}</td>
              <td className={styles.mono}>{sec.billableHours.toFixed(1)}h</td>
              <td className={styles.mono}>{sec.nonBillableHours.toFixed(1)}h</td>
              <td className={styles.mono}>{sec.totalHours.toFixed(1)}h</td>
            </tr>
          ))
        : null}
    </>
  );
}
